/**
 * Estado do módulo de radiossonda (Zustand). Registra o codec de URL `r`.
 */
import { create } from 'zustand';
import { registerUrlCodec } from '../state/urlCodec';
import type { DadosSondagem, SituacaoSondagem } from './types';

/** A camada começa desligada: é dado extra, não o assunto principal do app. */
export const DEFAULT_VISIVEL = false;
/** 8 desde 28/09/2026 (era 6): o usuário achou os símbolos pequenos. Eles crescem com o zoom pela regra comum (`map/escalaDoZoom.ts`). */
export const DEFAULT_TAMANHO = 8;
export const LIMITES_SONDAGEM = { tamanho: { min: 4, max: 14, step: 0.5 } } as const;

const VAZIO: DadosSondagem = { estacoes: [], perfisDe: new Map(), gerado: null };

export interface SoundingState {
  visivel: boolean;
  tamanho: number;
  situacao: SituacaoSondagem;
  dados: DadosSondagem;
  setVisivel: (on: boolean) => void;
  setTamanho: (t: number) => void;
  setSituacao: (s: SituacaoSondagem) => void;
  setDados: (d: DadosSondagem) => void;
}

function snap(v: number): number {
  const { min, max, step } = LIMITES_SONDAGEM.tamanho;
  if (!Number.isFinite(v)) return DEFAULT_TAMANHO;
  const c = Math.min(max, Math.max(min, v));
  return Math.round(Math.round(c / step) * step * 1000) / 1000;
}

export const useSoundingStore = create<SoundingState>()((set) => ({
  visivel: DEFAULT_VISIVEL,
  tamanho: DEFAULT_TAMANHO,
  situacao: { estado: 'idle', erro: null },
  dados: VAZIO,
  setVisivel: (visivel) => set({ visivel }),
  setTamanho: (t) => set({ tamanho: snap(t) }),
  setSituacao: (situacao) => set({ situacao }),
  setDados: (dados) => set({ dados }),
}));

/** `r=1` ligada, `t` tamanho se diferir do padrão. Desligada omite o parâmetro. */
export function encodeSounding(s: Pick<SoundingState, 'visivel' | 'tamanho'>): string | null {
  if (!s.visivel) return null;
  return s.tamanho === DEFAULT_TAMANHO ? '1' : `1t${s.tamanho}`;
}

export function decodeSounding(value: string): Pick<SoundingState, 'visivel' | 'tamanho'> | null {
  if (!value || value[0] !== '1') return null;
  const t = Number(/t([\d.]+)/.exec(value)?.[1]);
  return { visivel: true, tamanho: Number.isFinite(t) ? snap(t) : DEFAULT_TAMANHO };
}

registerUrlCodec({
  key: 'r',
  encode: () => encodeSounding(useSoundingStore.getState()),
  decode: (value) => {
    const s = decodeSounding(value);
    if (s) useSoundingStore.setState(s);
  },
  subscribe: (onChange) =>
    useSoundingStore.subscribe((a, b) => {
      if (a.visivel !== b.visivel || a.tamanho !== b.tamanho) onChange();
    }),
});
